import { Injectable } from '@angular/core';
import { EpSelfEnrolmentFormGroup } from './forms/ep-self-enrolment-form-group';
import { EpSelfEnrolmentFormFactory } from './forms/ep-self-enrolment-form-factory';
import { EpSelfEnrolmentRequest } from './requests/ep-self-enrolment.request';
import { EpSelfEnrolmentAddressRequest } from './requests/ep-self-enrolment-address.request';
import { EpSelfEnrolmentContactRequest } from './requests/ep-self-enrolment-contact.request';
import { AddressFormGroup } from '../../../../../forms/address-form-group';

@Injectable()
export class EpSelfEnrolmentFormService {
  public form: EpSelfEnrolmentFormGroup = EpSelfEnrolmentFormFactory.buildForm();

  public buildRequest(): EpSelfEnrolmentRequest {
    const { about, headOfficeAddress, mailingAddress, primaryContact, additionalContacts, logo } = this.form.controls;

    const aboutRaw = about.getRawValue();
    const mailingRaw = mailingAddress.getRawValue();
    const primaryRaw = primaryContact.getRawValue();

    const headOfficeAddressRequest = this.buildAddressRequest(headOfficeAddress);
    const mailingAddressFields = mailingRaw.sameAsHeadOffice
      ? headOfficeAddressRequest
      : this.buildAddressRequest(mailingAddress.controls.address);

    const primaryContactRequest: EpSelfEnrolmentContactRequest = {
      firstName: primaryRaw.firstName?.trim() ?? '',
      lastName: primaryRaw.lastName?.trim() ?? '',
      jobTitle: primaryRaw.jobTitle?.trim() ?? null,
      email: primaryRaw.email?.trim() ?? '',
      phoneNumberCountryCode: primaryRaw.phoneNumberCountryCode?.trim() ?? '',
      phoneNumber: primaryRaw.phoneNumber?.trim() ?? '',
      contactTopic: null,
      hasSigningAuthority: primaryRaw.hasSigningAuthority ?? false,
      primaryContact: true,
      address: this.buildAddressRequest(primaryContact.controls.address)
    };

    const additionalContactRequests: EpSelfEnrolmentContactRequest[] = additionalContacts.controls.map(contactForm => {
      const contactRaw = contactForm.getRawValue();
      return {
        firstName: contactRaw.firstName?.trim() ?? '',
        lastName: contactRaw.lastName?.trim() ?? '',
        jobTitle: contactRaw.jobTitle?.trim() ?? null,
        email: contactRaw.email?.trim() ?? '',
        phoneNumberCountryCode: contactRaw.phoneNumberCountryCode?.trim() ?? '',
        phoneNumber: contactRaw.phoneNumber?.trim() ?? '',
        contactTopic: contactRaw.contactTopic?.trim() ?? null,
        hasSigningAuthority: contactRaw.hasSigningAuthority ?? false,
        primaryContact: false,
        address: contactRaw.hasSigningAuthority ? this.buildAddressRequest(contactForm.controls.address) : null
      };
    });

    const logoFile = logo.value?.[0] ?? null;

    return {
      name: aboutRaw.name?.trim() ?? '',
      shortName: aboutRaw.shortName?.trim() ?? '',
      craPayrollRemittanceAccountNumber: aboutRaw.craPayrollRemittanceAccountNumber?.trim() ?? '',
      headOfficeAddress: headOfficeAddressRequest,
      mailingAddress: {
        attention: mailingRaw.attention?.trim() ?? null,
        ...mailingAddressFields
      },
      contacts: [primaryContactRequest, ...additionalContactRequests],
      logo: logoFile ? { attachmentTypeId: 1, fileName: logoFile.name, mediaType: logoFile.type } : undefined
    };
  }

  private buildAddressRequest(addressForm: AddressFormGroup): EpSelfEnrolmentAddressRequest {
    const raw = addressForm.getRawValue();
    return {
      addressLine1: raw.addressLine1?.trim() ?? '',
      addressLine2: raw.addressLine2?.trim() ?? null,
      addressLine3: raw.addressLine3?.trim() ?? null,
      city: raw.city?.trim() ?? '',
      stateId: raw.stateId ?? 0,
      countryId: raw.countryId ?? 0,
      postalCode: raw.postalCode?.trim() ?? ''
    };
  }
}
